// src/pages/super-admin/CompanyUsagePage.js - 기업 LLM 사용량 페이지

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendarAlt, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { getCompanyUsage } from '../../services/api';
import './CompanyUsagePage.scss';

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getThisMonthRange = () => {
  const now = new Date();
  return {
    start: formatDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: formatDateInputValue(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

const getLastMonthRange = () => {
  const now = new Date();
  return {
    start: formatDateInputValue(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    end: formatDateInputValue(new Date(now.getFullYear(), now.getMonth(), 0)),
  };
};

const getLastDaysRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return {
    start: formatDateInputValue(start),
    end: formatDateInputValue(end),
  };
};

const formatNumber = (value) => {
  const number = Number(value || 0);
  return number.toLocaleString('ko-KR');
};

const formatUsd = (value) => {
  const number = Number(value || 0);
  return `$${number.toLocaleString('en-US', {
    minimumFractionDigits: number > 0 && number < 0.01 ? 6 : 2,
    maximumFractionDigits: 8,
  })}`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function CompanyUsagePage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const defaultRange = useMemo(() => getThisMonthRange(), []);

  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('daily');
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);

  const fetchUsage = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      const params = { period };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const data = await getCompanyUsage(companyId, params);
      setUsage(data);
    } catch (err) {
      console.error('기업 LLM 사용량 조회 실패:', err);
      setError(err.message || 'LLM 사용량을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [companyId, endDate, period, startDate]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const totals = usage?.totals || {};
  const maxPeriodWeightedTokens = Math.max(
    1,
    ...(usage?.by_period || []).map((item) => Number(item.billable_weighted_tokens || 0))
  );

  const applyRange = (range) => {
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const renderEmpty = (label) => (
    <div className="usage-empty-inline">{label}</div>
  );

  return (
    <div className="company-usage-page">
      <div className="sa-page-header">
        <div className="sa-page-header-left">
          <button
            className="sa-btn sa-btn-secondary sa-btn-sm"
            onClick={() => navigate(-1)}
            style={{ marginRight: 12 }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1 className="sa-page-title">{usage?.company?.name || `기업 #${companyId}`}</h1>
            <p className="sa-page-subtitle">LLM 사용량 및 비용</p>
          </div>
        </div>
      </div>

      <div className="usage-filter-panel">
        <div className="usage-filter-row">
          <FontAwesomeIcon icon={faCalendarAlt} className="usage-filter-icon" />
          <input
            type="date"
            className="sa-search-input usage-date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span className="usage-date-separator">~</span>
          <input
            type="date"
            className="sa-search-input usage-date-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <select
            className="sa-filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="daily">일별</option>
            <option value="monthly">월별</option>
          </select>
          <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={fetchUsage}>
            조회
          </button>
        </div>
        <div className="usage-range-actions">
          <button type="button" onClick={() => applyRange(getLastDaysRange(7))}>최근 7일</button>
          <button type="button" onClick={() => applyRange(getThisMonthRange())}>이번 달</button>
          <button type="button" onClick={() => applyRange(getLastMonthRange())}>지난 달</button>
        </div>
      </div>

      {loading && <div className="sa-loading">불러오는 중...</div>}

      {error && (
        <div className="sa-empty">
          <div className="sa-empty-icon">!</div>
          <div className="sa-empty-text">{error}</div>
          <button className="sa-btn sa-btn-secondary" onClick={fetchUsage}>다시 시도</button>
        </div>
      )}

      {!loading && !error && usage && (
        <>
          <div className="usage-stats-grid">
            <div className="sa-stat-card">
              <div className="sa-stat-label">잔여 Weighted Tokens</div>
              <div className="sa-stat-value accent">{formatNumber(usage.remaining_weighted_tokens)}</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-label">사용 Weighted Tokens</div>
              <div className="sa-stat-value accent">{formatNumber(totals.billable_weighted_tokens)}</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-label">총 Raw Tokens</div>
              <div className="sa-stat-value">{formatNumber(totals.total_tokens)}</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-label">추정 비용</div>
              <div className="sa-stat-value">{formatUsd(totals.estimated_cost_usd)}</div>
            </div>
          </div>

          <div className="usage-stats-grid usage-secondary-grid">
            <div className="sa-stat-card">
              <div className="sa-stat-label">요청 수</div>
              <div className="sa-stat-value">{formatNumber(totals.request_count)}</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-label">입력 토큰</div>
              <div className="sa-stat-value">{formatNumber(totals.prompt_tokens)}</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-label">출력 토큰</div>
              <div className="sa-stat-value">{formatNumber(totals.completion_tokens)}</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-label">캐시/Reasoning 토큰</div>
              <div className="sa-stat-value">
                {formatNumber(totals.cached_input_tokens)} / {formatNumber(totals.reasoning_tokens)}
              </div>
            </div>
          </div>

          <section className="usage-section">
            <div className="usage-section-header">
              <h3><FontAwesomeIcon icon={faChartLine} /> 기간별 사용량</h3>
              <span>{usage.window?.start_date || '-'} ~ {usage.window?.end_date || '-'}</span>
            </div>
            {(usage.by_period || []).length === 0 ? renderEmpty('해당 기간에 사용량 데이터가 없습니다.') : (
              <div className="usage-period-list">
                {usage.by_period.map((item) => {
                  const value = Number(item.billable_weighted_tokens || 0);
                  const width = Math.max(2, Math.round((value / maxPeriodWeightedTokens) * 100));
                  return (
                    <div className="usage-period-row" key={item.period}>
                      <div className="usage-period-label">{item.period}</div>
                      <div className="usage-period-bar-track">
                        <div className="usage-period-bar" style={{ width: `${width}%` }} />
                      </div>
                      <div className="usage-period-value">{formatNumber(value)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="usage-section">
            <div className="usage-section-header">
              <h3>모델별 사용량</h3>
            </div>
            {(usage.by_model || []).length === 0 ? renderEmpty('모델별 사용량 데이터가 없습니다.') : (
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Model</th>
                    <th>요청 수</th>
                    <th>Raw Tokens</th>
                    <th>Weighted Tokens</th>
                    <th>추정 비용</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.by_model.map((item) => (
                    <tr key={`${item.provider}-${item.model}`}>
                      <td>{item.provider || '-'}</td>
                      <td style={{ fontWeight: 600 }}>{item.model || '-'}</td>
                      <td>{formatNumber(item.request_count)}</td>
                      <td>{formatNumber(item.total_tokens)}</td>
                      <td>{formatNumber(item.billable_weighted_tokens)}</td>
                      <td>{formatUsd(item.estimated_cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="usage-section">
            <div className="usage-section-header">
              <h3>사용자별 사용량</h3>
            </div>
            {(usage.by_user || []).length === 0 ? renderEmpty('사용자별 데이터가 없습니다.') : (
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>사용자</th>
                    <th>이메일</th>
                    <th>요청 수</th>
                    <th>Weighted Tokens</th>
                    <th>추정 비용</th>
                    <th>마지막 사용일시</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.by_user.map((item) => (
                    <tr key={item.user_id || 'unknown'}>
                      <td style={{ fontWeight: 600 }}>{item.name || `#${item.user_id || 'N/A'}`}</td>
                      <td style={{ fontSize: 13, color: '#374151' }}>{item.email || '-'}</td>
                      <td>{formatNumber(item.request_count)}</td>
                      <td>{formatNumber(item.billable_weighted_tokens)}</td>
                      <td>{formatUsd(item.estimated_cost_usd)}</td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{formatDateTime(item.last_used_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default CompanyUsagePage;
